// ── AD SPEND (Meta) ─────────────────────────────────────────────────────────
// Moved here from AdminTraffic.jsx: the Traffic tab is about where traffic came
// from, and this is about what it cost — two questions, two tabs. Rendered by
// the Ad Metrics tab beside the paid funnel figures.
//
// TWO DATE BASES ON ONE TAB, DELIBERATELY. Spend, watchers and stop rate here
// are ALL TIME (back to June — Ben wants the full pool history). The paid
// funnel figures above them are floored at 14 Aug. Both bases are stated on
// screen so nobody has to guess.
import { useState } from 'react';
import { C, formatDaySpan, RangeDates } from './adminShared';

// ── SPEND ON A CAMPAIGN NO CARD SHOWS ───────────────────────────────────────
// Renders nothing when the list is empty, which is the normal state.
//
// WHY THIS SITS AT THE TOP OF THE TAB. On 20 Aug a duplicated Starting 5
// campaign spent A$102.75 that appeared on no card, and nothing anywhere told
// Ben. The backend's /utm-stats `maintenance` block never reaches the screen at
// all, so a signal that only lived there would have been just as silent. This
// one is on the tab where the money is, above the figures it contradicts.
//
// It names the campaign id because that is what Ben pastes into Ads Manager to
// find out what it is, and the spend because that is what decides how urgent
// it is.
const UnclaimedCampaigns = ({ list }) => {
  if (!list || list.length === 0) return null;
  const total = list.reduce((n, c) => n + (Number(c.spend) || 0), 0);
  return (
    <div style={{ background: C.amberBg, border: '1px solid #F5D69A', borderRadius: 10, padding: '12px 14px', marginBottom: 12, fontSize: 11.5, color: '#92650A', lineHeight: 1.6 }}>
      <strong>
        {list.length === 1 ? 'A campaign is' : `${list.length} campaigns are`} spending money that no card below is showing.
      </strong>{' '}
      {money(total)} in total. The figures below exclude it, so they understate what was spent.
      Most often this means a campaign was duplicated in Ads Manager — add it to
      CAMPAIGN_CARDS in metaAdsSummary.js (and adNames.js) to fold it back in.
      <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
        {list.map(c => (
          <li key={c.campaignId} style={{ fontFamily: 'monospace', fontSize: 10.5 }}>
            {c.campaignId} — {money(c.spend)} · {c.firstDay} to {c.lastDay}
            {c.name ? ` · tagged "${c.name}"` : ''}
          </li>
        ))}
      </ul>
    </div>
  );
};

// ── META AD SPEND ───────────────────────────────────────────────────────────
// Each campaign is its OWN CARD, not a row in a shared table, because the three
// campaigns have different jobs and are therefore judged on different measures.
// Power Content buys attention (25% watchers), 4A nurtures it (stop rate too),
// Starting 5 converts it (cost per opt-in). A shared table would force one set
// of columns onto three questions.
//
// THE HONEST LIMIT, AGREED WITH BEN: warm-up spend cannot be attributed to
// individual leads. Per-ad figures tune each ad against ITS OWN job; only the
// blended cost per client answers "was the spend worth it". Nothing here should
// grow a per-ad cost-per-client.

// All money is AUD. One formatter so a card and a tile cannot disagree.
const money = (n) =>
  n == null || !Number.isFinite(Number(n))
    ? '—'
    : `A$${Number(n).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// EVERY derived figure goes through here. A zero denominator renders '—', never
// 0 (reads as free), never NaN, never Infinity.
const ratio = (num, den) => (den > 0 && Number.isFinite(num / den) ? num / den : null);
const pct   = (num, den) => { const r = ratio(num, den); return r == null ? '—' : `${Math.round(r * 100)}%`; };
const costOf = (spend, count) => money(ratio(spend, count));

// ── ONE DATE BASIS PER SECTION ──────────────────────────────────────────────
// The two-column Since/All-time treatment is gone. Each section now carries ONE
// basis and states it on screen:
//
//   Opt-in page  — since 14 Aug. Landings do not exist before then, so that is
//                  the only period an opt-in figure can describe.
//   Sales page   — since 14 Aug, same floor, unchanged.
//   Ad metrics   — ALL TIME. Spend, impressions, watchers and stop rate are
//                  facts about the ads themselves and go back to June; Ben
//                  wants the full history, not a comparison.
//
// Mixing bases inside one figure is what produced the impossible 94% earlier,
// so any figure here that cannot be all-time is either removed (it lives in
// Section 1) or computed wholly from the since range AND labelled.
const NUM_W = 92;

// One labelled row. `note` sits UNDER the label in the open — it carries the
// stop-rate definition and the cost-line workings, which must never be hidden
// in a tooltip.
const Line = ({ label, note, value, strong }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, padding: '6px 0', borderBottom: `1px solid ${C.border}` }}>
    <span style={{ flex: 1, minWidth: 0, fontSize: 11.5, color: strong ? C.heading : C.text, fontWeight: strong ? 700 : 500 }}>
      {label}
      {note && (
        <span style={{ display: 'block', fontSize: 9, color: C.accent, fontWeight: 500, lineHeight: 1.35, marginTop: 1 }}>
          {note}
        </span>
      )}
    </span>
    <span style={{ width: NUM_W, textAlign: 'right', fontSize: 12.5, fontWeight: 700, color: C.heading, whiteSpace: 'nowrap' }}>
      {value}
    </span>
  </div>
);

// ── OTHER ADS, PER CARD ─────────────────────────────────────────────────────
// This campaign's non-nominated ads. Replaces the old "Ads (N active)"
// expander AND the single shared block that used to sit below all three cards.
//
// Each row states its OWN status: an active-but-not-nominated ad is a live ad
// still spending money, and labelling it "paused" would simply be wrong.
const OtherAdsBlock = ({ all }) => {
  const [open, setOpen] = useState(false);
  const list = all;
  if (!list || list.count === 0) return null;

  return (
    <div style={{ marginTop: 6, borderTop: `1px solid ${C.border}`, paddingTop: 6 }}>
      <div
        onClick={() => setOpen(o => !o)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(o => !o); } }}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, cursor: 'pointer', userSelect: 'none', padding: '2px 0' }}
      >
        <span style={{ fontSize: 11, fontWeight: 700, color: C.accent, minWidth: 0 }}>
          Other ads ({list.count})
          <span style={{ display: 'block', fontSize: 9, color: C.accent, fontWeight: 500, marginTop: 1 }}>
            {list.activeCount} active, not nominated · {list.pausedCount} paused
          </span>
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: C.heading }}>{money(list.spend)}</span>
          <span style={{ fontSize: 11, color: C.accent, display: 'inline-block', transform: open ? 'rotate(180deg)' : 'none' }}>▼</span>
        </span>
      </div>
      {open && (
        <div>
          {list.ads.map(a => (
            <div key={a.adId} style={{ display: 'flex', gap: 6, borderTop: `1px solid ${C.border}`, padding: '5px 0 4px' }}>
              <span style={{ flex: 1, minWidth: 0, fontSize: 10.5, color: C.text, wordBreak: 'break-word' }}>
                {a.label}
                <span style={{ display: 'block', fontSize: 9, color: a.active ? C.green : C.accent, fontWeight: 600, marginTop: 1 }}>
                  {a.active ? 'active · not nominated' : 'paused'}
                </span>
              </span>
              <span style={{ width: NUM_W, textAlign: 'right', fontSize: 10.5, fontWeight: 700, color: C.heading }}>{money(a.spend)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// One campaign card, both columns.
//
// SPEND IS WHOLE-CAMPAIGN — every ad, nominated or not. Ben spent all of it.
// COST PER OPT-IN IS ALSO WHOLE-CAMPAIGN, on BOTH sides — see the block below.
// EVERY OTHER FIGURE IS THE NOMINATED AD ALONE, and the card says so.
// Cost per 25% watcher still divides the NOMINATED AD'S OWN SPEND by its own
// watchers: mixing campaign money with one ad's watchers would describe nothing.
// That objection does not apply to cost per opt-in, where both sides moved
// together.
// CLOSED BY DEFAULT. Three open cards is a long scroll on a phone before Ben
// reaches the one he came for, so the header carries enough to choose by — the
// campaign name, what it is for, and its all-time spend — and he opens one.
const CampaignCard = ({ since, all, stopRate, spanText }) => {
  const [open, setOpen] = useState(false);
  const nom = all.nominated || since.nominated;
  // Landings do not exist before 14 Aug, so anything derived from them has no
  // honest all-time value. Words, not a number and not a bare dash.

  const stopOf = (c) => (nom ? pct(c[stopRate.metric], c.impressions) : '—');

  return (
    <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: '12px 12px', marginBottom: 10 }}>
      <div
        onClick={() => setOpen(o => !o)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(o => !o); } }}
        style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, cursor: 'pointer', userSelect: 'none' }}
      >
        <span style={{ minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 800, color: C.heading }}>{all.label}</span>
          <span style={{ display: 'block', fontSize: 10.5, color: C.accent, marginTop: 1 }}>{all.subtitle}</span>
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
          <span style={{ textAlign: 'right' }}>
            <span style={{ display: 'block', fontSize: 8.5, color: C.accent, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Spend · {spanText}
            </span>
            <span style={{ display: 'block', fontSize: 13, fontWeight: 800, color: C.heading }}>{money(all.spend)}</span>
          </span>
          <span style={{ fontSize: 12, color: C.accent, display: 'inline-block', transform: open ? 'rotate(180deg)' : 'none' }}>▼</span>
        </span>
      </div>

      {!open ? null : (
      <>
      <div style={{ fontSize: 10, color: C.accent, lineHeight: 1.45, background: C.barTrack, borderRadius: 7, padding: '6px 8px', margin: '8px 0' }}>
        {nom ? (
          /* Only the Starting 5 card carries cost per opt-in, so only it may
             claim the figure is whole-campaign. Saying so on a card that has
             no such line would describe something that is not there. */
          <>Spend{all.measures.includes('costPerOptIn') && (
            <> and <strong style={{ color: C.text, fontWeight: 700 }}>cost per opt-in</strong></>
          )} {all.measures.includes('costPerOptIn') ? 'are' : 'is'} the
          {' '}<strong style={{ color: C.text, fontWeight: 700 }}>whole campaign</strong>. Every other figure is
          {' '}<strong style={{ color: C.text, fontWeight: 700 }}>{nom.label}</strong> only — the ad you nominated.</>
        ) : (
          <>No ad is nominated for this campaign, so there are no performance figures to show. Nominate one in adNames.js.</>
        )}
      </div>

      <Line label="Spend" note="whole campaign, every ad" value={money(all.spend)} strong />
      {nom && (
        <Line label="Nominated ad spend" value={money(all.nominated ? all.nominated.spend : 0)} />
      )}

      {all.measures.includes('stopRate') && (
        <Line label="Stop rate" note={stopRate.label} value={stopOf(all)} />
      )}
      {all.measures.includes('p25') && (
        <Line label="Watched 25%+" value={nom ? all.p25.toLocaleString('en-AU') : '—'} />
      )}
      {all.measures.includes('costPerP25') && (
        <Line
          label="Cost per 25% watcher"
          note={nom ? 'nominated ad spend ÷ its watchers' : null}
          value={nom ? costOf(all.nominated?.spend, all.p25) : '—'}
        />
      )}

      {/* ── THE ONE EXCEPTION TO THIS CARD'S ALL-TIME BASIS ──────────────────
          Landed and Opted in % are GONE from these cards. They are opt-in
          figures and they now live in Section 1, per campaign, where they have
          one honest basis; repeating them here is the duplication this
          restructure removes.

          Cost per opt-in stays, because it is the whole point of the Starting 5
          card and exists nowhere else — but it is computed ENTIRELY from the
          since-14-Aug range, numerator and denominator together, and labelled
          as such. An all-time spend over a since-14-Aug opt-in count is exactly
          the mismatch that produced the impossible 94% earlier. */}
      {/* NOT GATED ON A NOMINATED AD any more. Both sides of this ratio are now
          whole-campaign, so it is computable whether or not Ben has nominated
          anybody — gating it on the nominee would hide a figure that does not
          depend on one. Every other performance line stays gated, because those
          genuinely are the nominated ad's own numbers. */}
      {all.measures.includes('costPerOptIn') && (
        <>
          {/* THE NOTE STATES WHY 14 AUG, NOT A CLAIM ABOUT WHAT IS POSSIBLE.
              It used to read "the only period opt-ins can be attributed", which
              is false: leads carry their own utm_* columns captured at form
              submit, so opt-ins have been attributable to an ad since 7 Aug —
              the day the first Starting 5 campaign was created. What began on
              14 Aug was LANDING tracking (funnel_events), a different system.
              14 Aug is still the right floor, for the reason now given: that is
              when the ads switched from one generic tag to their own, so it is
              the first period whose per-ad figures mean anything. The FIGURE
              and its range are unchanged. */}
          {/* ── WHOLE CAMPAIGN, BOTH SIDES ────────────────────────────────
              This used to read the NOMINATED AD's spend over the NOMINATED
              AD's opt-ins (A$128.93 ÷ 48 = A$2.69). Both halves described one
              ad, so the ratio was internally consistent but it was not what
              Ben spent to get an opt-in — it silently omitted every other ad in
              the campaign and the opt-ins they produced.

              It is now the whole campaign over the whole campaign
              (A$232.96 ÷ 67 = A$3.48), which is the real cost of an opt-in.
              The figure goes UP, because the money the other ads spent is now
              in the numerator.

              `campaignOptedIn` includes opt-ins attributed to the campaign but
              not to any single ad (extra.optedIn), so the denominator really is
              everyone the campaign brought in, not just the per-ad sum.

              BOTH SIDES COME FROM THE SAME `since` RANGE OBJECT, so they cannot
              drift onto different windows — which is the mismatch that produced
              the impossible 94% before. */}
          <Line label="Opted in" note="whole campaign, since 14 Aug" value={since.campaignOptedIn.toLocaleString('en-AU')} />
          <Line
            label="Cost per opt-in"
            note={`whole campaign, since 14 Aug · ${money(since.spend)} ÷ ${since.campaignOptedIn} opt-ins`}
            value={costOf(since.spend, since.campaignOptedIn)}
            strong
          />
        </>
      )}

      <OtherAdsBlock all={all.otherAds} />
      </>
      )}
    </div>
  );
};

// One or two YYYY-MM-DD strings → "20 Aug" or "20–21 Aug". Only ever called on
// metaAds.windowSpend.days, which is already sorted ascending.
const fmtDayRange = (days) => {
  const fmt = (d) => {
    const dt = new Date(`${d}T00:00:00Z`);
    return `${dt.getUTCDate()} ${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][dt.getUTCMonth()]}`;
  };
  if (!days || days.length === 0) return 'no day yet';
  return days.length === 1 ? fmt(days[0]) : `${fmt(days[0])}–${fmt(days[days.length - 1])}`;
};

// `activeRangeId`: the id of the timeframe currently selected on the Ad
// Metrics tab's funnel section above (Pages & Masterclass's shared control,
// via onTimeframeChange — see AdminAdMetrics.jsx). The honesty block below
// renders ONLY for 'last24h' — every other timeframe leaves this section
// exactly as it was.
const MetaSpend = ({ metaAds, activeRangeId }) => {
  if (!metaAds) {
    return (
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '12px 14px', marginBottom: 14, fontSize: 11.5, color: C.accent, lineHeight: 1.55 }}>
        Ad spend isn’t available — the Meta poll hasn’t stored any figures yet.
        The traffic numbers above are unaffected.
      </div>
    );
  }

  const since = (metaAds.ranges || []).find(r => r.key === 'since');
  const all   = (metaAds.ranges || []).find(r => r.key === 'all');
  if (!since || !all) return null;

  return (
    <div style={{ marginBottom: 16 }}>
      <UnclaimedCampaigns list={metaAds.unclaimedCampaigns} />
      {/* ── "Last 24 hours" honesty block — 24h ONLY ──────────────────────────
          ry_meta_ad_daily is one row per ad per DAY, refreshed every 6 hours —
          there is no intraday spend to slice into a true rolling window. So
          this shows the REAL spend of whichever day row(s) the 24-hour window
          overlaps (never prorated, never an invented hourly split), and says
          in plain English why it won't tie out exactly to the funnel figures
          above, which ARE a true rolling 24-hour count. */}
      {activeRangeId === 'last24h' && metaAds.windowSpend && (
        <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: '10px 12px', marginBottom: 12 }}>
          <Line label="Ad spend (last 24 hours)" value={money(metaAds.windowSpend.spend)} strong />
          <div style={{ fontSize: 10, color: C.accent, fontStyle: 'italic', marginTop: 6, lineHeight: 1.5 }}>
            Ad spend is recorded per day and refreshed every few hours, so it won't line up exactly
            with the 24-hour funnel numbers above — this is the spend recorded for {fmtDayRange(metaAds.windowSpend.days)}.
          </div>
        </div>
      )}

      <div style={{ fontSize: 12, fontWeight: 700, color: C.accent, textTransform: 'uppercase', letterSpacing: '0.08em', margin: '4px 0 4px', padding: '0 4px' }}>
        What it cost
      </div>
      {/* ⚠ THIS SECTION IS ON A DIFFERENT DATE BASIS FROM THE FUNNEL FIGURES
          ABOVE IT, and the two spans are printed separately on purpose. Spend
          goes back to the first polled day; the funnel above starts at the
          launch floor. One date line at the top of the tab covering both would
          be wrong about whichever half it did not describe.
          These dates come from the SAME object as the figures, so a label here
          cannot drift from the numbers it sits under. */}
      <RangeDates text={formatDaySpan(all.since, all.until)} style={{ marginTop: 0, marginBottom: 8, padding: '0 4px' }} />

      {/* ALL TIME. Cost per client is BLENDED across all campaigns on purpose —
          warm-up spend cannot be attributed to one lead, so a per-campaign
          version would be a fiction.

          Cost per opt-in here is the ONE tile on a since-14-Aug basis, and it
          says so: opt-ins can only be attributed from the day landing tracking
          began, so its numerator and denominator are both taken from that
          range. All-time spend over since-14-Aug opt-ins would be the same
          mismatch that produced the impossible 94% earlier. */}
      <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: '10px 12px', marginBottom: 12 }}>
        <Line label="Spent" note={`${formatDaySpan(all.since, all.until)} · ${money(all.totals.spendPerDay)} / day over ${all.days} days`} value={money(all.totals.spend)} strong />
        <Line
          label="Cost per opt-in"
          note={`${formatDaySpan(since.since, since.until)} · blended · ${money(since.totals.spend)} ÷ ${since.totals.optedIn} opt-ins`}
          value={costOf(since.totals.spend, since.totals.optedIn)}
        />
        {/* BOTH SIDES ON THE SAME WINDOW — this divided ALL-TIME spend by a
            client count floored at 14 Aug, and called the result "all time".
            Harmless only while clients is 0; wrong the first time one closes,
            and wrong invisibly, because the label looked fine.
            SINCE 14 AUG WON, not all time, because the floor cannot honestly
            move: all 28 ry_purchase_complete rows predate 14 Aug and are
            pre-launch testing, so an "all time" denominator would invent 28
            clients rather than find early real ones. It also matches the cost
            per opt-in tile directly above, which is deliberately 14 Aug on both
            sides for the same kind of reason. Same shape of note, too, so the
            arithmetic is visible rather than implied. */}
        <Line
          label="Cost per client"
          note={since.totals.clients > 0
            ? `${formatDaySpan(since.since, since.until)} · blended · ${money(since.totals.spend)} ÷ ${since.totals.clients} client${since.totals.clients === 1 ? '' : 's'}`
            : 'no clients yet'}
          value={costOf(since.totals.spend, since.totals.clients)}
        />
      </div>

      <div style={{ fontSize: 10, color: C.accent, lineHeight: 1.5, margin: '0 0 10px', padding: '0 4px' }}>
        <strong style={{ color: C.text, fontWeight: 700 }}>All time</strong> — spend, impressions,
        watchers and stop rate go back to June, which is the point of this section: it measures the
        ads, not the funnel. The opt-in and client lines are the exception and are marked
        <strong style={{ color: C.text, fontWeight: 700 }}> since 14 Aug</strong>, because that is
        when the ads got their own tracking tags, so it is the first period whose per-ad figures
        mean anything. Where traffic came from, and how it converted, is the Opt-in page section.
      </div>

      {all.campaigns.map(c => (
        <CampaignCard
          key={c.id}
          all={c}
          since={since.campaigns.find(x => x.id === c.id) || c}
          stopRate={metaAds.stopRate}
          spanText={formatDaySpan(all.since, all.until)}
        />
      ))}
    </div>
  );
};



export { MetaSpend };
